<template>
  <div class="keybox-edit-panel">
    <!-- Header -->
    <div class="mb-2 d-flex align-items-center">
      <feather-icon icon="EditIcon" size="20" class="text-primary"/>
      <h4 class="mb-0 ml-50">{{ msg('Edit Keybox') }}</h4>
    </div>

    <!-- Card with details -->
    <b-card>
      <b-row>
        <!-- Basic Info -->
        <b-col cols="12" md="12">
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group :label="msg('IP Address / Hostname')">
                <b-form-input v-model="keybox.ipAddress" placeholder="192.168.0.10:PORT"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group :label="msg('Serial Number')">
                <b-form-input v-model="keybox.serialNumber" placeholder="06e79d9d00720f31"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group :label="msg('Device Pass')">
                <b-form-input v-model="keybox.devicePass" placeholder="111111"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group :label="msg('Location')">
                <b-form-input v-model="keybox.location"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group :label="msg('Status')">
                <b-form-select v-model="keybox.status" :options="statuses"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group :label="msg('Installation Status')">
                <b-form-select v-model="keybox.installationStatus" :options="installationStatuses"/>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <hr/>

      <!-- Assignments -->
      <h5 class="mt-2"><i class="fas fa-building text-primary mr-1"></i>{{ msg('Assignments') }}</h5>
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group :label="msg('Branch')">
            <v-select
                v-model="keybox.buid"
                :options="allBranches"
                class="w-100"
                label="name"
                :reduce="(branch) => branch.id"
                :clearable="false"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group :label="msg('Building')">
            <building-select-box v-model="keybox.buildingId"/>
          </b-form-group>
        </b-col>
      </b-row>

      <hr/>

      <!-- Compartments -->
      <div class="d-flex justify-content-between align-items-center">
        <h5 class="mt-2 mb-0">
          <i class="fas fa-box text-primary mr-1"></i>{{ msg('Slots') }}
        </h5>

      </div>

      <b-row class="mt-1 align-items-center">
        <b-col cols="6" class="d-flex align-items-center">
          <b-button size="sm" variant="secondary" @click="checkStatus">
            <i class="fas fa-question-circle mr-50"></i>
            {{ msg('Refresh status') }}
          </b-button>

          <div class="ml-2 d-flex align-items-center flex-wrap">
            <b-badge pill
                     :variant="currentStatusVariant"
                     class="px-2 py-1 font-weight-bold text-uppercase"
            >
              <i :class="currentStatusIcon" class="mr-50"></i>
              {{ currentStatusLabel }}

              <span
                  v-if="keyBoxStatus && keyBoxStatus.msg"
                  class="small  ml-2"
                  style="white-space: nowrap;"
              >
    {{ keyBoxStatus.msg }}
  </span>
            </b-badge>


          </div>

        </b-col>

        <b-col cols="6" class="text-right">
          <b-button size="sm" variant="warning" @click="openAllCompartments">
            <i class="fas fa-unlock-alt mr-50"></i>
            {{ msg('Unlock all') }}
          </b-button>
        </b-col>
      </b-row>


      <b-row v-if="compartments.length" class="mt-2">
        <b-col
            v-for="comp in compartments"
            :key="comp.number"
            cols="12"
            md="6"
            lg="4"
            class="mb-3"
        >
          <b-card
              class="p-3 shadow-sm rounded-3 border-0 hover-card transition-all"
              style="background-color: #f9f9fb;"
          >
            <!-- Header: Slot number -->
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0 text-primary font-weight-bold">
                <i class="fas fa-box mr-1"></i>{{ msg('Slot') }} #{{ comp.number }}
              </h6>
              <b-badge v-if="comp.isOpen!=null" :variant="comp.isOpen ? 'success' : 'danger'" pill>
                <i :class="comp.isOpen ? 'fas fa-door-open mr-1' : 'fas fa-door-closed mr-1'"></i>
                {{ comp.isOpen ? msg('Open') : msg('Closed') }}
              </b-badge>

              <b-badge v-else variant="secondary" pill>
                <i class="fas fa-question-circle mr-1"></i>
                {{ msg('Unknown') }}
              </b-badge>
            </div>

            <!-- Status line -->
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-muted small">{{ msg('Status') }}</span>
              <b-badge v-if="comp.isOccupied!=null" :variant="comp.isOccupied ? 'warning' : 'secondary'" pill>
                <i :class="comp.isOccupied ? 'fas fa-user mr-1' : 'fas fa-user-slash mr-1'"></i>
                {{ comp.isOccupied ? msg('Occupied') : msg('Free') }}
              </b-badge>
              <b-badge v-else variant="secondary" pill>
                <i class="fas fa-question-circle mr-1"></i>
                {{ msg('Unknown') }}
              </b-badge>
            </div>

            <div class="assigned-room-glass p-3 rounded-lg mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0 text-info">
                  <i class="fas fa-building mr-1"></i>{{ msg('Assigned Room') }}
                </h6>
              </div>

              <real-estate-picker-locker
                  :buildings="[keybox.buildingId]"
                  v-model="comp.realEstateId"
              />
            </div>

            <!-- Actions -->
            <div class="d-flex justify-content-end mt-2">
              <b-button
                  size="sm"
                  variant="warning"
                  class="px-3 shadow-sm"
                  :disabled="comp.isOpen"
                  @click="openCompartment(comp)"
              >
                <i class="fas fa-unlock-alt mr-1"></i>{{ msg('Unlock') }}
              </b-button>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-card>

    <!-- Save -->
    <div class="text-right mt-2">
      <b-button variant="outline-primary" class="px-3" @click="updateKeybox">
        <feather-icon icon="SaveIcon" size="16" class="mr-50"/>
        {{ msg('Save Changes') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import BuildingSelectBox from "@/components/components/picker/buildingSelectBox.vue";
import RealEstatePickerLocker from "@/components/components/picker/realEstatesPickerLocker.vue";

export default {
  name: 'KeyboxEditPanel',
  components: {RealEstatePickerLocker, BuildingSelectBox},
  props: {
    id: {type: String, required: true}
  },
  data() {
    return {
      keybox: {compartments: [], totalCompartments: 0},
      keyBoxStatus: {},
      currentStatus: 'connected', // possible values: connected, disconnected, error, checking
      compartments: [],
      statuses: [
        {value: 'online', text: 'Online'},
        {value: 'offline', text: 'Offline'},
        {value: 'error', text: 'Error'}
      ],
      installationStatuses: [
        {value: 'installed', text: 'Installed'},
        {value: 'pending', text: 'Pending'},
        {value: 'failed', text: 'Failed'}
      ]
    }
  },
  computed: {
    ...mapGetters('data', ['getAllBranches']),
    currentStatusLabel() {
      switch (this.currentStatus) {
        case 'connected':
          return this.msg('Connected');
        case 'disconnected':
          return this.msg('Disconnected');
        case 'error':
          return this.msg('Error');
        case 'checking':
          return this.msg('Checking...');
        default:
          return this.msg('Unknown');
      }
    },
    currentStatusVariant() {
      switch (this.currentStatus) {
        case 'connected':
          return 'success';
        case 'disconnected':
          return 'secondary';
        case 'error':
          return 'danger';
        case 'checking':
          return 'warning';
        default:
          return 'dark';
      }
    },
    currentStatusIcon() {
      switch (this.currentStatus) {
        case 'connected':
          return 'fas fa-check-circle text-success';
        case 'disconnected':
          return 'fas fa-plug text-secondary';
        case 'error':
          return 'fas fa-exclamation-triangle text-danger';
        case 'checking':
          return 'fas fa-sync-alt fa-spin text-warning';
        default:
          return 'fas fa-question-circle text-dark';
      }
    },
    allBranches() {
      return Object.values(this.getAllBranches)
    }
  },
  created() {
    this.loadKeybox()
  },
  methods: {
    ...mapActions('keybox', ['getKeyboxById', 'update', 'openSlot', 'openAllSlots', 'checkStatusLocker']),

    async loadKeybox() {
      const data = await this.getKeyboxById({id: this.id})
      this.keybox = data
      this.compartments = [...(data.compartments || [])]
      this.checkStatus();
    },

    openCompartment(comp) {
      let $this = this;
      this.openSlot({keyboxId: this.keybox.id, slotNumber: comp.number})
          .then(() => {
            $this.loadKeybox()
          })
    },

    openAllCompartments() {
      let $this = this;
      this.openAllSlots({keyboxId: this.keybox.id})
          .then(() => {
            $this.loadKeybox()
          })
    },

    checkStatus() {
      this.currentStatus = 'checking';

      this.checkStatusLocker({ keyboxId: this.id })
          .then(ans => {
            this.keyBoxStatus = ans;

            const code = ans.code || 0;
            const message = (ans.msg || '').toLowerCase();
            const hasData = Array.isArray(ans.data) && ans.data.length > 0;

            // === Interpret backend result ===
            if (code === 1 && hasData) {
              this.currentStatus = 'connected'; // ✅ device online and returning data
            } else if (message.includes('offline') || code === 0) {
              this.currentStatus = 'error'; // 🔴 device offline or unreachable
            } else {
              this.currentStatus = 'disconnected'; // ⚪ fallback case
            }
          })
          .catch(() => {
            this.currentStatus = 'error'; // network or request error
          });
    },


    updateKeybox() {
      this.keybox.compartments = this.compartments

      let $this = this

      this.update({keybox: this.keybox})
          .then(
              (id) => {

                $this.$emit('submitted', {status: true, server: id, id})

              },
              (a) => $this.$emit('submitted', {
                status: false,
                server: a
              })
          )
    },
  }
}
</script>

<style scoped>
.keybox-edit-panel {
  padding: 10px;
}

h5 {
  font-weight: 600;
  margin-bottom: 10px;
}

/* === Shared transition and hover behavior === */
.hover-card {
  transition: all 0.25s ease-in-out;
  border-radius: 1rem !important;
}

.hover-card:hover {
  transform: translateY(-3px);
}

/* === Light Layout === */
body:not(.dark-layout) .hover-card {
  background-color: #ffffff !important;
  color: #4b4b4b !important;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}

body:not(.dark-layout) .hover-card:hover {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
}

/* === Dark Layout === */
.dark-layout .hover-card {
  background-color: #283046 !important; /* Vuexy dark card */
  color: #d0d2d6 !important; /* Vuexy text-gray-light */
  border: 1px solid #3b4253 !important; /* subtle card border */
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.55);
  transition: all 0.3s ease-in-out;
}

.dark-layout .hover-card:hover {
  background-color: #303850 !important; /* slightly brighter on hover */
  border-color: #444c5e !important;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.7);
}

/* Texts, badges, and muted tones inside cards */
.dark-layout .hover-card .text-muted {
  color: #b4b7bd !important;
}

.dark-layout .hover-card h5,
.dark-layout .hover-card h6,
.dark-layout .hover-card strong {
  color: #e7e9ed !important;
}

/* Buttons blend with Vuexy primary tones */
.dark-layout .hover-card .btn-success {
  background-color: #28c76f !important;
  border: none;
  color: #fff;
}

.dark-layout .hover-card .btn-success:disabled {
  background-color: #28c76f !important;
  opacity: 0.6;
}


/* Badge contrast tweaks */
.dark-layout .b-badge.bg-success,
.dark-layout .b-badge.badge-success {
  background-color: #28c76f !important;
  color: #fff !important;
}

.dark-layout .b-badge.bg-danger,
.dark-layout .b-badge.badge-danger {
  background-color: #ea5455 !important;
}

.dark-layout .b-badge.bg-warning,
.dark-layout .b-badge.badge-warning {
  background-color: #ff9f43 !important;
  color: #1e1f24 !important;
}

.dark-layout .b-badge.bg-secondary,
.dark-layout .b-badge.badge-secondary {
  background-color: #6c757d !important;
}


.dark-layout .b-form-group label {
  color: #b0b0b0 !important;
}

/* Buttons */
.dark-layout .btn-success {
  background-color: #28c76f !important;
  border: none !important;
}

.dark-layout .btn-success:disabled {
  opacity: 0.6;
  background-color: #28c76f !important;
}

.assigned-room-glass {
  backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.dark-layout .assigned-room-glass {
  background: rgba(25, 28, 46, 0.7);
  border-color: rgba(255, 255, 255, 0.05);
}

</style>
